import apicore, { resolveUserToken } from '@/helpers/apicore';
import { createLobbySocket } from '@/helpers/apicore/lobby';
import {
  adaptConfig,
  adaptGatherInfo,
  adaptLanChallenges,
  adaptListItem,
  adaptPlayer,
  adaptPlayers,
  createTickRequestOrder,
  intToTeam,
  lanChallengesErrorKey,
  leaderAfterLeave,
  serverStateFromStatus,
  shouldQueryLanChallenges,
  statusToInt,
  upsertAdaptedPlayer,
  upsertConfigRow,
} from '@/helpers/apicore/adapters';
import config from '@/helpers/config';
import dataStore from '@/helpers/data';
import helperError from '@/helpers/error';
import language from '@/helpers/language';
import router from '@/router';

// Non-reactive handles (never in state): the live WS, the 10s fallback poll while the
// WS is down, the 10s list poll, and a one-shot warn flag for the unset-guild case.
let lobbySocket = null;
let fallbackTimer = null;
let listTimer = null;
let listWarned = false;
// LAN-challenge tick GET/PUT ordering (see createTickRequestOrder).
const lanOrder = createTickRequestOrder();

const state = {
  list: [],
  owner: { userId: '', name: '' },
  me: '', // own discord_id (from GET /v2/me), used for owner-gating + the ready write
  info: {
    id: 0, // numeric gather id — all C2 sub-resources key by it; captured from by-token
    leader: '',
    name: '',
    token: '',
    joinedPlayers: 0,
    size: 0,
    size0: 0,
    size1: 0,
  },
  config: [],
  players: [],
  server: {
    ready: false,
    status: 0,
    locked: false,
    loading: false,
    started: false,
    name: '',
    tv: '',
    ip: '',
    password: '',
    connectString: '',
  },
  paused: false,
  isPageVisible: true,
  // LAN challenge tick (addendum 2026-09-24 §6). api-core decides `visible`; hidden and
  // unticked until it says otherwise.
  lanChallenges: {
    visible: false,
    ticked: false,
    saving: false,
  },
};

const getters = {
  gather: (state) => state,
};

// Correct error-message resolution (the legacy handlers passed `dataStore.language`,
// which is undefined and throws inside the catch — this uses the real getter).
const errText = (ctx) => language(dataStore.getter(ctx.getters).language.current)['errorOccured'];
// api-core answers 503 when it cannot read player ratings from the stats
// service. It refuses to balance rather than quietly applying a random split,
// so that case gets its own message instead of the generic one.
const balanceErrText = (ctx) => language(dataStore.getter(ctx.getters).language.current)['balanceTeamsError'];

const lanChallengesQueryable = () => shouldQueryLanChallenges(state.info.id, state.me, state.info.leader, state.server.status);

// Apply an api-core answer, re-checked against the local hide-only gate: the page may
// have seen the gather leave `open` (or the leader change) while the request was out.
const applyLanChallenges = (commit, body) => {
  commit('SET_LAN_CHALLENGES', adaptLanChallenges(lanChallengesQueryable() ? body : null));
};

// Re-read the LAN-challenge tick state (gate: shouldQueryLanChallenges). Whether the
// leader sees the box is api-core's call. A failed read hides the box (fail closed); the
// stored tick itself is untouched by that.
const refreshLanChallenges = (commit) => {
  const current = lanOrder.beginRead();
  if (!lanChallengesQueryable()) {
    commit('SET_LAN_CHALLENGES', adaptLanChallenges(null));
    return;
  }
  apicore.get('gathers/' + state.info.id + '/lan-challenges').then(
    (res) => {
      if (current()) applyLanChallenges(commit, res.data);
    },
    () => {
      if (current()) applyLanChallenges(commit, null);
    }
  );
};

// Re-derive the owner {userId, name} from the current adapted player list + leader id.
const refreshOwner = (commit) => {
  const leaderId = String(state.info.leader);
  const row = state.players.filter((p) => p.userId === leaderId)[0];
  if (row) {
    commit('SET_OWNER', { userId: row.userId, name: row.userName });
  } else {
    // leader not in the roster (edge) — still gate by id so the leader keeps controls.
    commit('SET_OWNER', { userId: leaderId, name: '' });
  }
};

// Apply a full gather state (by-token response, WS snapshot, or fallback-poll refetch).
// payload: { gather, players, config, connect_string } (R10 top-level connect_string;
// R19 snapshot = full state replace).
const applyGatherState = (commit, ctx, payload) => {
  const g = payload.gather;
  const players = adaptPlayers(payload.players || []);
  commit('SET_PLAYERS', players);
  commit('SET_INFO', adaptGatherInfo(g, players.length));
  commit('SET_INFO_ID', g && g.id ? g.id : 0);
  commit('SET_CONFIG', adaptConfig(payload.config || {}));
  const st = serverStateFromStatus(statusToInt(g ? g.status : ''), payload.connect_string);
  commit('SET_SERVER_STATUS', st.status);
  commit('SET_SERVER_STARTED', st.started);
  commit('SET_SERVER_LOCKED', st.locked);
  commit('SET_SERVER_CONNECT', st.connectString);
  if (st.status === 2) {
    commit('SET_PAUSED', true); // parity with legacy gameover (gather.js:566-568)
  }
  refreshOwner(commit);
  // A snapshot can carry a new leader or status — both change the tick visibility.
  refreshLanChallenges(commit);
  // Same hook the legacy info action used (gather.js:218): drive the map-selection
  // TYPE (random|voting|oscar, R16) into the map store without a network write.
  ctx.dispatch('map/type', { type: g ? g.map_selection : 'random', send: false });
};

const stopFallbackPoll = () => {
  if (fallbackTimer) {
    clearInterval(fallbackTimer);
    fallbackTimer = null;
  }
};

// Re-read the full gather state over REST and apply it like a snapshot.
const resyncGatherState = (commit, ctx, token) => {
  const params = {};
  if (config.apicore.guildId) params.guild_id = config.apicore.guildId;
  apicore.get('gathers/by-token/' + encodeURIComponent(token), { params }).then(
    (res) => {
      applyGatherState(commit, ctx, res.data);
    },
    () => {
      // transient; the next snapshot / poll tick recovers. WS reconnect is the primary path.
    }
  );
};

const startFallbackPoll = (commit, ctx, token) => {
  stopFallbackPoll();
  fallbackTimer = setInterval(() => {
    if (!state.isPageVisible) return;
    resyncGatherState(commit, ctx, token);
  }, 10000);
};

const handleDelta = (commit, ctx, type, data) => {
  switch (type) {
    case 'player_joined':
    case 'player_updated': {
      const list = upsertAdaptedPlayer(state.players, adaptPlayer(data));
      commit('SET_PLAYERS', list);
      commit('SET_INFO_JOINEDPLAYERS', list.length);
      refreshOwner(commit);
      break;
    }
    case 'player_left': {
      const list = state.players.filter((p) => p.userId !== String(data.discord_id));
      if (list.length < 1) {
        router.push({ name: 'ViewGatherList' }); // parity with gather.js:255
      } else {
        commit('SET_PLAYERS', list);
        commit('SET_INFO_JOINEDPLAYERS', list.length);
        // api-core recomputes the leader on a leave (earliest remaining joiner) and clears
        // the LAN-challenge tick when it changes. Contract rev 2 §4: player_left carries
        // the leader AFTER the leave. An api-core without that field → if the leader is
        // the one who left, re-read the gather instead.
        const leaderAfter = leaderAfterLeave(data);
        if (leaderAfter !== undefined) {
          if (leaderAfter !== String(state.info.leader)) {
            commit('SET_INFO_LEADER', leaderAfter);
            refreshLanChallenges(commit);
          }
          refreshOwner(commit);
        } else {
          refreshOwner(commit);
          if (String(data.discord_id) === String(state.info.leader) && state.info.token) {
            resyncGatherState(commit, ctx, state.info.token);
          }
        }
      }
      break;
    }
    case 'config_updated': {
      commit('SET_CONFIG', upsertConfigRow(state.config, data.key, data.value));
      break;
    }
    case 'status_changed': {
      const st = serverStateFromStatus(statusToInt(data.status), data.connect_string);
      commit('SET_SERVER_STATUS', st.status);
      commit('SET_SERVER_STARTED', st.started);
      commit('SET_SERVER_LOCKED', st.locked);
      // R10: connect_string is only present when the server becomes connect-ready;
      // absent means "keep whatever we have", never blank it out.
      if (data.connect_string !== undefined && data.connect_string !== null) {
        commit('SET_SERVER_CONNECT', data.connect_string);
      }
      if (data.status === 'finished') {
        commit('SET_PAUSED', true);
      }
      refreshLanChallenges(commit); // leaving `open` hides the tick
      break;
    }
    case 'gather_cancelled': {
      ctx.dispatch('notification/text', { type: 'warning', msg: 'This gather was cancelled.' });
      router.push({ name: 'ViewGatherList' });
      break;
    }
    // votes_updated / suggestions_updated: no voting UI (SettingsGather.vue:11-12) → ignore.
    default:
      break;
  }
};

// Shared /v2/gathers list fetch. Extracted from the `list` action so the
// on-becoming-visible catch-up (`listRefresh`) can reuse it WITHOUT going
// through `list`, which clears the poll timer on entry.
// `store` is the root store (namespaced ctx.dispatch cannot reach 'app/*').
const LIST_POLL_MS = 10000;

const fetchList = (store, commit) => {
  const guildId = config.apicore.guildId;
  if (!guildId) {
    commit('SET_LIST', []);
    if (!listWarned) {
      listWarned = true;
      // eslint-disable-next-line no-console
      console.warn('[gather] VUE_APP_GUILD_ID is unset — /play list stays empty until the guild is pinned at build (F4).');
    }
    return;
  }
  store.dispatch('app/loading', true);
  apicore.get('gathers', { params: { guild_id: guildId, status: 'open' } }).then(
    (res) => {
      store.dispatch('app/loading', false);
      const gathers = res && res.data && res.data.gathers ? res.data.gathers : [];
      commit('SET_LIST', gathers.map(adaptListItem));
    },
    (error) => {
      store.dispatch('app/loading', false);
      helperError(store, error, errText(store));
    }
  );
};

const openLobby = (commit, ctx, token) => {
  if (lobbySocket) {
    lobbySocket.close();
    lobbySocket = null;
  }
  lobbySocket = createLobbySocket({
    token,
    getUserToken: resolveUserToken, // re-read per (re)connect, not captured
    onSnapshot: (payload) => applyGatherState(commit, ctx, payload),
    onDelta: (type, payload) => handleDelta(commit, ctx, type, payload),
    onDown: () => startFallbackPoll(commit, ctx, token),
    onUp: () => stopFallbackPoll(),
  });
};

// api-core action error → user-facing message. R18/U2: 409 already_confirmed and 429
// cooldown both mean "the gather is past the point of a restart" → tell the user to
// stop and open a new one instead of leaving a bare "error occurred".
const actionErrorMessage = (error, ctx) => {
  const res = error && error.response ? error.response : null;
  const status = res ? res.status : 0;
  const detail = res && res.data ? res.data.detail : '';
  if (status === 409 && detail === 'already_confirmed') {
    return 'This gather is already live — stop it, then open a new gather to play again.';
  }
  if (status === 429) {
    return 'Starting too quickly — please wait a moment before trying again.';
  }
  return errText(ctx);
};

const actions = {
  token({ commit }, token) {
    commit('SET_TOKEN', token);
  },

  reset({ commit }) {
    if (lobbySocket) {
      lobbySocket.close();
      lobbySocket = null;
    }
    stopFallbackPoll();
    if (listTimer) {
      clearInterval(listTimer);
      listTimer = null;
    }
    commit('SET_TOKEN', '');
    commit('SET_LIST', []);
    commit('SET_INFO', undefined);
    commit('SET_INFO_ID', 0);
    commit('SET_ME', '');
    commit('SET_OWNER', { userId: '', name: '' });
    commit('SET_PLAYERS', []);
    commit('SET_CONFIG', []);
    commit('SET_SERVER', { tv: '', name: '', ip: '', password: '' });
    commit('SET_SERVER_CONNECT', '');
    commit('SET_SERVER_LOADING', false);
    commit('SET_SERVER_STARTED', false);
    commit('SET_SERVER_LOCKED', true);
    // Drop any in-flight LAN-challenge response for the old gather.
    lanOrder.invalidate();
    commit('SET_LAN_CHALLENGES', adaptLanChallenges(null));
    this.dispatch('page/name', '');
    this.dispatch('map/reset');
    this.dispatch('match/reset');
  },

  // Enter a gather page: snapshot → identity → maps → live WS. Replaces `runner`.
  async enter({ commit }) {
    const token = state.info.token;
    if (!token) return;
    this.dispatch('app/loading', true);
    const params = {};
    if (config.apicore.guildId) params.guild_id = config.apicore.guildId; // R6: optional
    try {
      const res = await apicore.get('gathers/by-token/' + encodeURIComponent(token), { params });
      applyGatherState(commit, this, res.data);
    } catch (error) {
      this.dispatch('app/loading', false);
      helperError(this, error, errText(this));
      return;
    }
    // Own identity (F1/R14): C2 keys everything by discord_id. Held in gather.me and
    // read directly by the gather components' owner-gating / self-row checks. Deliberately
    // NOT written into user.userId (the legacy numeric id-space) — user/check and
    // user/login overwrite user.userId on every load, and on a hard-refresh/deep-link
    // into /play/:token that overwrite races enter() and would silently clobber the
    // leader's identity (stripping their controls). Decoupled id-spaces avoid the race.
    try {
      const meRes = await apicore.get('me');
      const meId = meRes && meRes.data && meRes.data.discord_id ? String(meRes.data.discord_id) : '';
      commit('SET_ME', meId);
    } catch (e) {
      // identity unavailable → owner-gating degrades to read-only; not fatal.
    }
    // The snapshot above ran before `me` was known, so its LAN-challenge check hid the box.
    refreshLanChallenges(commit);
    this.dispatch('app/loading', false);
    this.dispatch('map/maps'); // needs info.id from the snapshot above
    openLobby(commit, this, token);
  },

  list({ commit }, currentState) {
    if (listTimer) {
      clearInterval(listTimer);
      listTimer = null;
    }
    if (currentState === 'stop') return;
    if (currentState === 'update' || currentState === 'once') {
      fetchList(this, commit);
    }
    if (currentState === 'update') {
      listTimer = setInterval(() => {
        if (state.isPageVisible) fetchList(this, commit);
      }, LIST_POLL_MS);
    }
  },

  // Immediate list refresh, for when the tab becomes visible again.
  //
  // The interval above is SUPPRESSED while the page is hidden (rate limiting),
  // so on return the list is exactly as stale as the moment you left — for up
  // to a full LIST_POLL_MS. Opening a gather in Discord and alt-tabbing back
  // therefore showed a list without it, and reloading was faster than waiting,
  // which is what a user actually does (reported 2026-09-03).
  //
  // Deliberately NOT `dispatch('list', 'once')`: `list` clears listTimer on
  // entry and only re-arms for 'update', so routing the refresh through it
  // would silently kill the poll it is meant to complement.
  listRefresh({ commit }) {
    // listTimer armed == the list page is mounted. Without this gate a tab
    // focus on any other page would fire a pointless request.
    if (!listTimer) return;
    fetchList(this, commit);
  },

  async team({ commit }, parameter) {
    this.dispatch('app/loading', true);
    if (!resolveUserToken()) {
      router.push({ name: 'ViewHome' });
      return;
    }
    return await apicore.patch('gathers/' + state.info.id + '/players/' + parameter.player, { team: intToTeam(parameter.team) }).then(
      (res) => {
        this.dispatch('app/loading', false);
        // Apply the returned Player immediately so the move feels instant even if the
        // WS is momentarily down; the WS player_updated delta re-applies idempotently.
        if (res && res.data) {
          commit('SET_PLAYERS', upsertAdaptedPlayer(state.players, adaptPlayer(res.data)));
          refreshOwner(commit);
        }
      },
      (error) => {
        this.dispatch('app/loading', false);
        helperError(this, error, errText(this));
      }
    );
  },

  async scramble({ commit }) {
    this.dispatch('app/loading', true);
    if (!resolveUserToken()) {
      router.push({ name: 'ViewHome' });
      return;
    }
    return await apicore.post('gathers/' + state.info.id + '/team-mix', {}).then(
      (res) => {
        this.dispatch('app/loading', false);
        const players = res && res.data && res.data.players ? res.data.players : [];
        commit('SET_PLAYERS', adaptPlayers(players));
        refreshOwner(commit);
      },
      (error) => {
        this.dispatch('app/loading', false);
        helperError(this, error, errText(this));
      }
    );
  },

  async balance({ commit }) {
    this.dispatch('app/loading', true);
    if (!resolveUserToken()) {
      router.push({ name: 'ViewHome' });
      return;
    }
    return await apicore.post('gathers/' + state.info.id + '/team-balance', {}).then(
      (res) => {
        this.dispatch('app/loading', false);
        const players = res && res.data && res.data.players ? res.data.players : [];
        commit('SET_PLAYERS', adaptPlayers(players));
        refreshOwner(commit);
      },
      (error) => {
        this.dispatch('app/loading', false);
        const ratingsUnavailable = error && error.response && error.response.status === 503;
        helperError(this, error, ratingsUnavailable ? balanceErrText(this) : errText(this));
      }
    );
  },

  // Kept for parity with the legacy action shape; no component dispatches it today.
  async ready({ commit }, parameter) {
    this.dispatch('app/loading', true);
    if (!resolveUserToken()) {
      router.push({ name: 'ViewHome' });
      return;
    }
    return await apicore.patch('gathers/' + state.info.id + '/players/' + state.me, { ready: !!parameter.ready }).then(
      () => {
        this.dispatch('app/loading', false);
        commit('SET_PAUSED', false);
      },
      (error) => {
        this.dispatch('app/loading', false);
        helperError(this, error, errText(this));
      }
    );
  },

  async update(_store, parameter) {
    this.dispatch('app/loading', true);
    if (!resolveUserToken()) {
      router.push({ name: 'ViewHome' });
      return;
    }
    // The EAV object {name,type,value,options?} goes verbatim into JSONB (F8/R3);
    // api-core preserves it, so send the caller's value object as-is.
    return await apicore.patch('gathers/' + state.info.id + '/config', { key: parameter.item.key, value: parameter.item.value }).then(
      () => {
        this.dispatch('app/loading', false);
      },
      (error) => {
        this.dispatch('app/loading', false);
        helperError(this, error, errText(this));
      }
    );
  },

  // Leader ticks/unticks "LAN challenges" (contract §4 PUT). The response is the new
  // server truth; on a refusal the box is re-read so it shows what api-core holds.
  async lanChallengesTick({ commit }, ticked) {
    if (!resolveUserToken()) {
      router.push({ name: 'ViewHome' });
      return;
    }
    const write = lanOrder.beginWrite();
    commit('SET_LAN_CHALLENGES_SAVING', true);
    let refused = false;
    try {
      const res = await apicore.put('gathers/' + state.info.id + '/lan-challenges', { ticked: ticked === true });
      if (write.current()) applyLanChallenges(commit, res.data);
    } catch (error) {
      refused = true;
      const res = error && error.response ? error.response : null;
      const key = res ? lanChallengesErrorKey(res.status, res.data ? res.data.detail : '') : null;
      const text = key ? language(dataStore.getter(this.getters).language.current)[key] : errText(this);
      helperError(this, error, text);
    } finally {
      write.end();
      commit('SET_LAN_CHALLENGES_SAVING', lanOrder.writing());
    }
    // After end(), so this re-read is allowed to land.
    if (refused && write.current()) refreshLanChallenges(commit);
  },

  async action({ commit }, parameter) {
    this.dispatch('app/loading', true);
    commit('SET_SERVER_LOADING', true);
    commit('SET_SERVER_LOCKED', true);
    if (!resolveUserToken()) {
      router.push({ name: 'ViewHome' });
      return;
    }
    // 'reload' was removed from C2 (start|stop|restart only, F7); the button is gone.
    return await apicore.post('gathers/' + state.info.id + '/action', { action: parameter.action }).then(
      () => {
        commit('SET_SERVER_LOADING', false);
        commit('SET_SERVER_STARTED', parameter.action === 'stop' ? false : true);
        commit('SET_SERVER_LOCKED', parameter.action === 'stop' ? false : true);
      },
      (error) => {
        // Re-enable the controls and re-lock to match the (unchanged) real status so a
        // failed start/restart doesn't wedge the buttons disabled.
        commit('SET_SERVER_LOADING', false);
        commit('SET_SERVER_LOCKED', state.server.status !== 0);
        helperError(this, error, actionErrorMessage(error, this));
      }
    );
  },
};

const mutations = {
  SET_LIST(state, payload) {
    state.list = payload;
  },
  SET_INFO(state, payload) {
    if (payload !== undefined) {
      // Adapted info object goes in directly (no legacy field-picking).
      state.info.leader = payload.leader;
      state.info.name = payload.name;
      state.info.token = payload.token;
      state.info.joinedPlayers = payload.joinedPlayers;
      state.info.size = payload.size;
      state.info.size0 = payload.size0;
      state.info.size1 = payload.size1;
    } else {
      state.info.leader = '';
      state.info.name = '';
      state.info.joinedPlayers = 0;
      state.info.size = 10;
      state.info.size0 = 5;
      state.info.size1 = 5;
    }
  },
  SET_INFO_ID(state, payload) {
    state.info.id = payload;
  },
  SET_INFO_LEADER(state, payload) {
    state.info.leader = payload;
  },
  SET_INFO_JOINEDPLAYERS(state, payload) {
    state.info.joinedPlayers = payload;
  },
  SET_ME(state, payload) {
    state.me = payload;
  },
  SET_PLAYERS(state, payload) {
    if (payload !== null) {
      state.players = payload;
    }
  },
  SET_OWNER(state, payload) {
    state.owner = payload;
  },
  SET_CONFIG(state, payload) {
    state.config = payload;
  },
  SET_SERVER(state, payload) {
    state.server.name = payload.name;
    state.server.tv = payload.tv;
    state.server.ip = payload.ip;
    state.server.password = payload.password;
  },
  SET_SERVER_CONNECT(state, payload) {
    state.server.connectString = payload;
  },
  SET_SERVER_STATUS(state, payload) {
    state.server.status = payload;
  },
  SET_SERVER_LOCKED(state, payload) {
    state.server.locked = payload;
  },
  SET_SERVER_LOADING(state, payload) {
    state.server.loading = payload;
  },
  SET_SERVER_STARTED(state, payload) {
    state.server.started = payload;
  },
  SET_TOKEN(state, payload) {
    state.info.token = payload;
  },
  SET_PAUSED(state, parameter) {
    state.paused = parameter;
  },
  SET_PAGE_VISIBILITY(state, isVisible) {
    state.isPageVisible = isVisible;
  },
  SET_LAN_CHALLENGES(state, payload) {
    state.lanChallenges.visible = payload.visible;
    state.lanChallenges.ticked = payload.ticked;
  },
  SET_LAN_CHALLENGES_SAVING(state, payload) {
    state.lanChallenges.saving = payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
